import { _ } from "libs";
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import cx from "classnames";
import RxInput from "./rxInput";

export type RenderFunction = (suggestion: any) => JSX.Element | JSX.Element[];
export type OnAutocompleteInputChange = (input: string) => any[] | Promise<any[]>;
type OnItemHover = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
type OnItemSelect = (suggestion: any) => void;
type GetSuggestionValue = (suggestion: any) => string;
type AutocompleteMenuProps = {
	suggestions: any[];
	renderFunction: RenderFunction;
	highlight?: number;
	onItemHover: OnItemHover;
	onItemClick: OnItemSelect;
};
const AutocompleteMenu = ({
	suggestions,
	renderFunction,
	highlight = null as number,
	onItemHover,
	onItemClick,
}: AutocompleteMenuProps) => {
	if (!suggestions) {
		return null;
	}

	return (
		<div className="autocompleteSuggestions">
			{suggestions.length === 0 ? (
				<div className="noResults">{_("navbarSearchAutocomplete.noResults")}</div>
			) : null}
			{suggestions.map((suggestion, key: number) => {
				const className = cx("autocompleteSuggestionCell", "cell", { highlighted: highlight === key });
				return (
					<div
						key={key}
						className={className}
						onMouseOver={onItemHover}
						data-suggestion-index={key}
						onClick={() => onItemClick(suggestion)}
					>
						{renderFunction(suggestion)}
					</div>
				);
			})}
		</div>
	);
};

type Props = {
	value?: string;
	onChange: OnAutocompleteInputChange;
	debounce?: number;
	placeholder?: string;
	renderSuggestion: RenderFunction;
	minLength?: number;
	onItemSelected?: OnItemSelect;
	closeOnSelect?: boolean;
	clearOnSelect?: boolean;
	replaceValueOnSelect?: boolean;
	getSuggestionValue?: GetSuggestionValue;
	inputProps?: any;
	emptyValue?: any[];
};

const AutocompleteInput = (
	{
		value = "",
		onChange,
		renderSuggestion,
		onItemSelected,
		debounce = 500,
		placeholder = "",
		minLength = 3,
		closeOnSelect = true,
		clearOnSelect = false,
		replaceValueOnSelect = true,
		getSuggestionValue,
		inputProps,
		emptyValue = [],
	}: Props,
	ref
) => {
	const node = useRef();
	const input = useRef();

	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [suggestions, setSuggestions] = useState(emptyValue);
	const [highlight, setHighlight] = useState(null as number);

	useEffect(() => {
		if (emptyValue.length > 0) {
			setSuggestions(emptyValue);
		}
	}, [emptyValue]);

	useImperativeHandle(ref, () => ({
		focus: () => {
			//@ts-ignore
			input.current.focus();
		},
		setValue: value => {
			//@ts-ignore
			input.current.setValue(value);
		},
		clear: () => {
			clear();
		},
	}));

	//https://codesandbox.io/s/9o3lw5792w
	const handleClickOutside = (e: MouseEvent) => {
		//@ts-ignore
		if (node.current.contains(e.target)) {
			// inside click
			return;
		}
		// outside click
		setOpen(false);
		setHighlight(null);
	};

	useEffect(() => {
		//@ts-ignore
		if (open) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]);

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (suggestions === undefined || suggestions.length === 0) {
			return;
		}

		if (e.key === "ArrowDown") {
			if (highlight === null) {
				setOpen(true);
				setHighlight(0);
			} else if (suggestions.length === highlight + 1) {
				setHighlight(0);
			} else {
				setHighlight(highlight + 1);
			}
		}

		if (e.key === "ArrowUp") {
			if (highlight === null) {
				setOpen(true);
				setHighlight(suggestions.length - 1);
			} else if (highlight === 0) {
				setHighlight(suggestions.length - 1);
			} else {
				setHighlight(highlight - 1);
			}
		}

		if (e.key === "Enter") {
			e.preventDefault();
			const suggestion = suggestions[highlight];
			processSuggestion(suggestion);
		}
	};

	const onFocus = () => {
		if (suggestions.length > 0) {
			setOpen(true);
			setHighlight(null);
		}
	};

	const onItemHover = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		const highlight = parseInt(e.currentTarget.dataset["suggestionIndex"]);
		setHighlight(highlight);
	};

	const handleChange = async (input: string) => {
		setLoading(true);
		let suggestions = [];

		if (!input) {
			suggestions = emptyValue;
		} else {
			suggestions = await onChange(input);
		}

		setLoading(false);
		setOpen(true);
		setSuggestions(suggestions);
	};

	const className = cx("autocompleteInput", { autocompleteLoading: loading });

	inputProps = {
		...inputProps,
		// className,
		onKeyDown,
		onFocus,
		placeholder,
	};

	const clear = (): void => {
		//@ts-ignore
		input.current.clear();
	};

	const processSuggestion = (suggestion: any): void => {
		if (!suggestion) {
			return;
		}
		if (onItemSelected) {
			onItemSelected(suggestion);
		}

		if (input.current && getSuggestionValue && replaceValueOnSelect) {
			//@ts-ignore
			input.current.setValue(getSuggestionValue(suggestion));
		}

		if (closeOnSelect) {
			setOpen(false);
		}

		if (clearOnSelect) {
			clear();
		}
	};

	return (
		<div className={className} ref={node}>
			<RxInput
				value={value}
				debounce={debounce}
				minLength={minLength}
				inputProps={inputProps}
				onChange={handleChange}
				ref={input}
			/>
			{open && !loading ? (
				<AutocompleteMenu
					suggestions={suggestions}
					renderFunction={renderSuggestion}
					highlight={highlight}
					onItemHover={onItemHover}
					onItemClick={processSuggestion}
				/>
			) : null}
		</div>
	);
};

export default forwardRef(AutocompleteInput);
